import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createSiteSchema, siteSchema, type CreateSite, type Site } from '@netdeck/contracts';
import { z } from 'zod';
import { apiRequest } from '../../shared/api/client';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { dateTimeFormat } from '../../shared/i18n/format';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { useUpdateSite } from '../nodes/api';
import { Button } from '../../shared/ui/Button';
import { DataTable, type Column } from '../../shared/ui/DataTable';
import { Field, FormActions, FormRow } from '../../shared/ui/Field';
import { EmptyState, ErrorState, Loading, Panel } from '../../shared/ui/Panel';

const sitesKey = ['sites'] as const;

export function SitesPage({ canWrite }: { canWrite: boolean }): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const language = currentLanguage();

  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  /**
   * Площадка, которую сейчас правят.
   *
   * Форма правки живёт там же, где форма добавления — над таблицей, а не
   * в отдельном окне: полей два, и модальное окно ради них закрывало бы
   * список, по которому человек и понимает, чем эта площадка отличается
   * от соседней.
   */
  const [editing, setEditing] = useState<Site | null>(null);

  const sites = useQuery({
    queryKey: sitesKey,
    queryFn: () => apiRequest('/sites', z.array(siteSchema)),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/sites/${id}`, z.unknown(), { method: 'DELETE', acceptStatuses: [204] }),
    onSuccess: (_result, id) => {
      // Открытая форма правки удалённой площадки предлагала бы сохранить
      // то, чего уже нет: сохранение ответило бы 404 без внятной причины.
      setEditing((site) => (site?.id === id ? null : site));
      void queryClient.invalidateQueries({ queryKey: sitesKey });
    },
  });

  const columns: Column<Site>[] = [
    {
      key: 'name',
      header: t('sites.column.name'),
      width: '1.4fr',
      render: (site) => <span className="font-medium">{site.name}</span>,
    },
    {
      key: 'description',
      header: t('sites.column.description'),
      width: '2fr',
      render: (site) => <span className="truncate text-muted">{site.description ?? '—'}</span>,
    },
    {
      key: 'created',
      header: t('sites.column.createdAt'),
      width: '1fr',
      render: (site) => (
        <span className="font-mono text-xs text-dim">
          {dateTimeFormat(language, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(
            new Date(site.createdAt),
          )}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('sites.column.actions'),
      width: '110px',
      align: 'right',
      render: (site) =>
        canWrite ? (
          <span className="inline-flex items-center justify-end gap-1">
            <Button
              icon="pencil"
              size="sm"
              variant="ghost"
              aria-label={t('sites.editAria', { name: site.name })}
              title={t('sites.editTitle')}
              onClick={() => {
                setAdding(false);
                setEditing(site);
              }}
            />
            <Button
              icon="trash"
              size="sm"
              variant="danger"
              disabled={remove.isPending}
              aria-label={t('sites.deleteAria', { name: site.name })}
              title={t('sites.deleteTitle')}
              onClick={() => {
                if (confirm(t('sites.deleteConfirm', { name: site.name }))) {
                  remove.mutate(site.id);
                }
              }}
            />
          </span>
        ) : (
          <span className="font-mono text-xs text-faint">—</span>
        ),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4">
      <Panel
        title={t('nav.sites')}
        subtitle={sites.data ? `${sites.data.length}` : undefined}
        actions={
          canWrite ? (
            <Button
              icon={adding ? 'close' : 'plus'}
              size="sm"
              onClick={() => {
                setEditing(null);
                setAdding((value) => !value);
              }}
            >
              {adding ? t('common.action.cancel') : t('sites.add')}
            </Button>
          ) : null
        }
      >
        {adding ? (
          <SiteForm
            onDone={() => {
              setAdding(false);
              void queryClient.invalidateQueries({ queryKey: sitesKey });
            }}
          />
        ) : null}

        {/*
          `key` обязателен: без него переход от одной правки к другой оставил бы
          в полях прежнюю площадку — состояние формы пережило бы смену строки.
        */}
        {editing ? (
          <SiteForm key={editing.id} existing={editing} onDone={() => setEditing(null)} />
        ) : null}

        {sites.isPending ? (
          <Loading />
        ) : sites.error ? (
          <ErrorState
            title={t('sites.loadFailed')}
            hint={t('sites.loadFailedHint', { reason: errorText(sites.error) })}
          />
        ) : (
          <DataTable
            columns={columns}
            rows={sites.data}
            rowKey={(site) => site.id}
            empty={
              <EmptyState
                title={t('sites.emptyTitle')}
                hint={t('sites.emptyHint')}
                action={
                  canWrite ? (
                    <Button icon="plus" size="sm" onClick={() => setAdding(true)}>
                      {t('sites.addFirst')}
                    </Button>
                  ) : null
                }
              />
            }
          />
        )}
      </Panel>
    </div>
  );
}

/**
 * Форма площадки: одна на создание и на правку.
 *
 * Не две похожие: поля у них те же самые, и разъехались бы они на первой же
 * правке — в одной появилось бы описание, в другой нет. Отличаются только
 * начальные значения, глагол на кнопке и запрос.
 *
 * Правится и описание, хотя просили название: поле уже стоит рядом в форме
 * добавления, приходит тем же PATCH и лечит ту же беду — опечатку, из-за
 * которой сегодня площадку приходится удалять и заводить заново вместе
 * со всей её картой.
 */
function SiteForm({ existing, onDone }: { existing?: Site; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');

  const create = useMutation({
    mutationFn: (input: CreateSite) =>
      apiRequest('/sites', siteSchema, { method: 'POST', body: input }),
    onSuccess: onDone,
  });

  // Готовая правка площадки: она же меняет шаг сетки с карты и кладёт ответ
  // прямо в кэш, поэтому новое имя видно и в списке, и в выборе площадки
  // на карте, без повторного запроса.
  const update = useUpdateSite();

  const saving = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  function submit(event: FormEvent): void {
    event.preventDefault();

    const input = {
      name,
      description: description.trim() === '' ? null : description,
    };

    // Схема одна на оба запроса: правка — это тот же набор полей,
    // объявленный частичным (`updateSiteSchema`), и вторая проверка
    // здесь означала бы вторую правду о том, что считать именем.
    const parsed = createSiteSchema.safeParse(input);

    if (!parsed.success) {
      return;
    }

    if (existing) {
      update.mutate({ siteId: existing.id, input: parsed.data }, { onSuccess: onDone });
      return;
    }

    create.mutate(parsed.data);
  }

  return (
    <FormRow onSubmit={submit}>
      <div className="w-full sm:w-[240px]">
        <Field
          label={t('sites.form.name')}
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('sites.form.namePlaceholder')}
        />
      </div>
      <div className="w-full sm:w-[320px]">
        <Field
          label={t('sites.form.description')}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder={t('sites.form.optional')}
        />
      </div>
      <FormActions>
        <Button
          icon={existing ? 'check' : 'plus'}
          type="submit"
          variant="primary"
          size="sm"
          disabled={saving || !name.trim()}
        >
          {existing
            ? saving
              ? t('sites.form.saving')
              : t('common.action.save')
            : saving
              ? t('sites.form.creating')
              : t('sites.form.create')}
        </Button>
        {existing ? (
          <Button size="sm" onClick={onDone} disabled={saving}>
            {t('common.action.cancel')}
          </Button>
        ) : null}
        {error ? <span className="text-sm text-down-fg">{errorText(error)}</span> : null}
      </FormActions>
    </FormRow>
  );
}
